import React, { useState, useEffect } from 'react';
import { fetchEnclavePublicKey, encryptForEnclave } from './enclaveEncryption';

export default function RFMResponseForm({ matchingRequest, onClose, onSubmit, wsClient }) {
  const { ticker, requestId, matching_window, execution_mode } = matchingRequest;
  const [timeLeft, setTimeLeft] = useState(matching_window);
  const [isExpired, setIsExpired] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wsStatus, setWsStatus] = useState('disconnected');

  const [formData, setFormData] = useState({
    bidQty: '',
    bidPrice: '',
    offerQty: '',
    offerPrice: ''
  });

  // Checkbox state for including bid/offer
  const [includeBid, setIncludeBid] = useState(false);
  const [includeOffer, setIncludeOffer] = useState(false);

  // Monitor WebSocket status
  useEffect(() => {
    if (wsClient) {
      if (wsClient.readyState === WebSocket.OPEN) {
        setWsStatus('connected');
      } else if (wsClient.readyState === WebSocket.CONNECTING) {
        setWsStatus('connecting');
      } else if (wsClient.readyState === WebSocket.CLOSED) {
        setWsStatus('disconnected');
      } else {
        setWsStatus('error');
      }
    }
  }, [wsClient]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setIsExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Auto-close when expired
  useEffect(() => {
    if (isExpired) {
      const timeout = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isExpired, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isExpired || isSubmitting) return;

    // Validate at least one of bid/offer is selected
    if (!includeBid && !includeOffer) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 3000);
      return;
    }

    // Validate that selected options have values
    if (includeBid && (!formData.bidQty || !formData.bidPrice)) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 3000);
      return;
    }
    if (includeOffer && (!formData.offerQty || !formData.offerPrice)) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('encrypting');

    try {
      console.log('[RFMResponseForm] Preparing matching response for encryption');

      // Build order data in the same format as the initiator
      const responseData = {
        requestId,
        ticker,
        timestamp: Date.now(),
        isBid: includeBid,
        isOffer: includeOffer,
        // Include bid fields only if bid is selected
        ...(includeBid && {
          bidQty: parseFloat(formData.bidQty),
          bidPrice: parseFloat(formData.bidPrice)
        }),
        // Include offer fields only if offer is selected
        ...(includeOffer && {
          offerQty: parseFloat(formData.offerQty),
          offerPrice: parseFloat(formData.offerPrice)
        })
      };

      console.log('[RFMResponseForm] Response data:', responseData);

      // Fetch enclave public key
      console.log('[RFMResponseForm] Fetching enclave public key...');
      setSubmitStatus('fetching_key');
      const publicKey = await fetchEnclavePublicKey();
      console.log('[RFMResponseForm] ✓ Public key fetched');

      // Encrypt the response data
      console.log('[RFMResponseForm] Encrypting response data...');
      setSubmitStatus('encrypting');
      const encryptedPayload = await encryptForEnclave(responseData, publicKey);
      console.log('[RFMResponseForm] ✓ Response data encrypted');

      // Submit to backend (use authToken from localStorage)
      const authToken = localStorage.getItem('authToken');
      console.log('[RFMResponseForm] Submitting encrypted payload to /api/enclave/encrypt...');
      setSubmitStatus('submitting');

      const response = await fetch('/api/enclave/encrypt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          encrypted_payload: encryptedPayload,
          submission_type: 'matching_response',
          request_id: requestId
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log('[RFMResponseForm] ========================================');
        console.log('[RFMResponseForm] ✓ MATCHING RESPONSE ENCRYPTED AND SUBMITTED!');
        console.log('[RFMResponseForm] Submission ID:', result.data.submission_id);
        console.log('[RFMResponseForm] Created at:', result.data.created_at);
        console.log('[RFMResponseForm] ========================================');

        setSubmitStatus('success');

        // Notify parent to show waiting progress modal
        if (onSubmit) {
          onSubmit({
            type: 'matching_response_submitted',
            requestId,
            submissionId: result.data.submission_id,
            waitingData: {
              ticker,
              timeRemaining: timeLeft,
              startTime: Date.now()
            }
          });
        }
        // Don't close - Dashboard will close us and show the waiting modal
      } else {
        throw new Error(result.error || 'Failed to submit encrypted response');
      }
    } catch (error) {
      console.error('[RFMResponseForm] Error encrypting/submitting response:', error);
      console.error('[RFMResponseForm] Error details:', error.message);
      setSubmitStatus('error');
      setIsSubmitting(false);

      // Show error for 3 seconds then allow retry
      setTimeout(() => {
        setSubmitStatus('');
      }, 3000);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (timeLeft > 30) return 'text-cyan-400';
    if (timeLeft > 10) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getWsStatusColor = () => {
    switch (wsStatus) {
      case 'connected': return 'bg-green-500';
      case 'connecting': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getWsStatusText = () => {
    switch (wsStatus) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'error': return 'Connection Error';
      default: return 'Disconnected';
    }
  };

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        {/* Header */}
        <div className="settings-header">
          <div>
            <h3>{ticker}</h3>
            <p className="settings-subtitle">
              Matching Request • {execution_mode ? 'Fast' : 'Secure'} Mode
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <div className={`text-3xl font-mono font-bold ${getTimerColor()} transition-colors`} style={{ fontSize: '28px', fontWeight: '700' }}>
              {formatTime(timeLeft)}
            </div>
            <p style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {isExpired ? 'EXPIRED' : 'REMAINING'}
            </p>
          </div>
        </div>

        {/* WebSocket Status Indicator */}
        <div style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0a0a0a',
          borderRadius: '8px',
          padding: '12px 16px',
          border: '1px solid #2a2a2a'
        }}>
          <span style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Connection Status</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className={`${getWsStatusColor()}`} style={{ width: '8px', height: '8px', borderRadius: '50%', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
            <span style={{ fontSize: '12px', color: '#e5e5e5' }}>{getWsStatusText()}</span>
          </div>
        </div>

        {/* Request ID */}
        <div style={{
          marginBottom: '24px',
          backgroundColor: '#0a0a0a',
          borderRadius: '8px',
          padding: '12px 16px',
          border: '1px solid #2a2a2a'
        }}>
          <span style={{ fontSize: '12px', color: '#999' }}>Request ID: </span>
          <span style={{ fontSize: '12px', color: '#e5e5e5', fontFamily: 'monospace' }}>{requestId}</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Bid Section */}
          <div className="settings-section" style={{
            backgroundColor: includeBid ? '#0d1b1b' : '#0a0a0a',
            borderRadius: '8px',
            border: includeBid ? '1px solid rgba(38, 166, 154, 0.3)' : '1px solid #2a2a2a',
            padding: '16px',
            marginBottom: '0',
            opacity: includeBid ? 1 : 0.6,
            transition: 'all 0.2s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeBid}
                  onChange={(e) => setIncludeBid(e.target.checked)}
                  disabled={isExpired}
                  style={{
                    width: '18px',
                    height: '18px',
                    accentColor: '#26a69a',
                    cursor: 'pointer'
                  }}
                />
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: includeBid ? '#26a69a' : '#666', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Bid</h3>
              </label>
              <span style={{ fontSize: '12px', color: '#999' }}>Buy</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#999', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Quantity
                </label>
                <input
                  type="number"
                  name="bidQty"
                  value={formData.bidQty}
                  onChange={handleInputChange}
                  disabled={isExpired || !includeBid}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  style={{
                    width: '100%',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: includeBid ? '#e5e5e5' : '#666',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#26a69a'}
                  onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#999', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Price (USD)
                </label>
                <input
                  type="number"
                  name="bidPrice"
                  value={formData.bidPrice}
                  onChange={handleInputChange}
                  disabled={isExpired || !includeBid}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  style={{
                    width: '100%',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: includeBid ? '#e5e5e5' : '#666',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#26a69a'}
                  onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                />
              </div>
            </div>
          </div>

          {/* Offer Section */}
          <div className="settings-section" style={{
            backgroundColor: includeOffer ? '#1b0d0d' : '#0a0a0a',
            borderRadius: '8px',
            border: includeOffer ? '1px solid rgba(239, 83, 80, 0.3)' : '1px solid #2a2a2a',
            padding: '16px',
            marginBottom: '0',
            opacity: includeOffer ? 1 : 0.6,
            transition: 'all 0.2s ease'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeOffer}
                  onChange={(e) => setIncludeOffer(e.target.checked)}
                  disabled={isExpired}
                  style={{
                    width: '18px',
                    height: '18px',
                    accentColor: '#ef5350',
                    cursor: 'pointer'
                  }}
                />
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: includeOffer ? '#ef5350' : '#666', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Offer</h3>
              </label>
              <span style={{ fontSize: '12px', color: '#999' }}>Sell</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#999', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Quantity
                </label>
                <input
                  type="number"
                  name="offerQty"
                  value={formData.offerQty}
                  onChange={handleInputChange}
                  disabled={isExpired || !includeOffer}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  style={{
                    width: '100%',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: includeOffer ? '#e5e5e5' : '#666',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ef5350'}
                  onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#999', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Price (USD)
                </label>
                <input
                  type="number"
                  name="offerPrice"
                  value={formData.offerPrice}
                  onChange={handleInputChange}
                  disabled={isExpired || !includeOffer}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  style={{
                    width: '100%',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '6px',
                    padding: '10px 12px',
                    color: includeOffer ? '#e5e5e5' : '#666',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ef5350'}
                  onBlur={(e) => e.target.style.borderColor = '#2a2a2a'}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isExpired || isSubmitting || (!includeBid && !includeOffer)}
            className="save-btn"
            style={{
              width: '100%',
              marginTop: '8px',
              opacity: (isExpired || isSubmitting || (!includeBid && !includeOffer)) ? '0.5' : '1',
              cursor: (isExpired || isSubmitting || (!includeBid && !includeOffer)) ? 'not-allowed' : 'pointer',
              backgroundColor: (isExpired || isSubmitting || (!includeBid && !includeOffer)) ? '#2a2a2a' : '#26a69a'
            }}
          >
            {isExpired
              ? 'TIME EXPIRED'
              : (!includeBid && !includeOffer)
                ? 'SELECT BID OR OFFER'
                : isSubmitting
                  ? submitStatus === 'fetching_key'
                    ? '🔑 FETCHING KEY...'
                    : submitStatus === 'encrypting'
                      ? '🔐 ENCRYPTING...'
                      : submitStatus === 'submitting'
                        ? '📤 SUBMITTING...'
                        : 'PROCESSING...'
                  : 'SUBMIT ENCRYPTED RESPONSE'
            }
          </button>
        </form>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: 'rgba(38, 166, 154, 0.1)',
            border: '1px solid rgba(38, 166, 154, 0.3)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#26a69a', fontSize: '14px', fontWeight: '500', margin: 0 }}>
              ✓ Encrypted response submitted successfully!
            </p>
            <p style={{ color: '#999', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
              Your bid/offer has been securely encrypted and sent to the enclave
            </p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: 'rgba(239, 83, 80, 0.1)',
            border: '1px solid rgba(239, 83, 80, 0.3)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#ef5350', fontSize: '14px', fontWeight: '500', margin: 0 }}>
              ✗ Failed to submit encrypted response
            </p>
            <p style={{ color: '#999', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
              Please check your connection and try again
            </p>
          </div>
        )}

        {isExpired && (
          <div style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: 'rgba(239, 83, 80, 0.1)',
            border: '1px solid rgba(239, 83, 80, 0.3)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#ef5350', fontSize: '14px', fontWeight: '500', margin: '0 0 12px 0' }}>
              Submission window has closed
            </p>
            <button
              onClick={onClose}
              style={{
                padding: '10px 24px',
                backgroundColor: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#444'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#333'}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}
